import { useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import Button from "common/components/Button/Button";
import TextArea from "common/components/TextArea/TextArea";
import useAuth from "common/hooks/useAuth";
import useItem from "common/hooks/useItem";
import useList from "common/hooks/useList";
import AlertsContext from "common/providers/alerts";
import { Comment } from "types/Comment";

/**
 * `modelName` used on the generic `Comment` model to attach notes to a saved
 * balance sheet.
 */
export const SAVED_BALANCE_SHEET_MODEL = "saved-balance-sheet";

type Props = {
  savedBalanceSheetId: number;
};

function BalanceSheetComments({ savedBalanceSheetId }: Props) {
  const { t } = useTranslation();
  const { setAlert } = useContext(AlertsContext);
  const { user: currentUser } = useAuth();

  const [content, setContent] = useState("");
  const [isSending, setIsSending] = useState(false);

  const listenToEvents = useMemo(
    () => [
      `new_comment_${SAVED_BALANCE_SHEET_MODEL}`,
      `deleted_comment_${SAVED_BALANCE_SHEET_MODEL}`,
    ],
    []
  );

  const { items, fetchItems, error } = useList<Comment>("comments/data/list", {
    defaultFilters: {
      entityId: savedBalanceSheetId,
      modelName: SAVED_BALANCE_SHEET_MODEL,
    },
    cachePrefix: `Comments_savedBalanceSheet_${savedBalanceSheetId}`,
    listenToEvents,
  });

  const { saveItem, removeItem } = useItem<Comment>("comments/data/list", "");

  useEffect(() => {
    if (error) {
      setAlert(error, "danger");
    }
  }, [error, setAlert]);

  const handleSend = async () => {
    const trimmed = content.trim();
    if (!trimmed) {
      return;
    }
    setIsSending(true);
    try {
      await saveItem({
        entityId: savedBalanceSheetId,
        modelName: SAVED_BALANCE_SHEET_MODEL,
        content: trimmed,
      });
      setContent("");
      fetchItems();
    } catch (e) {
      setAlert(e, "danger");
    } finally {
      setIsSending(false);
    }
  };

  const handleDelete = async (id: string | number) => {
    try {
      await removeItem(id);
      fetchItems();
    } catch (e) {
      setAlert(e, "danger");
    }
  };

  return (
    // Capped to the viewport so the notes get their own scrollbar instead of
    // stretching the sticky column past the fold.
    <div className="bg-white rounded-2xl border border-slate-150 p-4 flex flex-col gap-4 max-h-[calc(100vh-2rem)]">
      <h3 className="text-lg font-bold text-primary shrink-0">
        {t("balance.commentsTitle")}
      </h3>

      <div className="flex flex-col gap-2 shrink-0">
        <span className="text-sm text-slate-400">
          {t("balance.commentPlaceholder")}
        </span>
        <TextArea
          rows={4}
          value={content}
          className="text-input"
          onChange={(e: any) => setContent(e.target.value)}
        />
        <Button
          title={t("balance.addComment")}
          type="secondary"
          icon="IoIosSend"
          onClick={handleSend}
          loading={isSending}
          disabled={isSending || !content.trim()}
        />
      </div>

      <div
        className="flex flex-col gap-3 flex-1 min-h-0 overflow-y-auto pr-1
        scrollbar-thin scrollbar-thumb-slate-800/90 scrollbar-track-slate-200 scrollbar-thumb-rounded scrollbar-track-rounded"
      >
        {items && items.length ? (
          items.map((comment) => (
            <div
              key={comment.id}
              className="bg-slate-100 rounded-lg p-3 flex flex-col gap-1"
            >
              <div className="flex flex-row items-start justify-between gap-2">
                <span className="text-sm font-bold text-primary">
                  {comment.authorUsername}
                </span>
                {comment.authorId === currentUser?.id && (
                  <Button
                    type="error-line"
                    icon="TrashIcon"
                    compact
                    onClick={() => handleDelete(comment.id)}
                    confirmMessage={t("actions.confirmDelete")}
                  />
                )}
              </div>
              <span className="text-xs text-slate-400">
                {dayjs(comment.createdAt).format("LLL")}
              </span>
              <p className="text-sm whitespace-pre-line">{comment.content}</p>
            </div>
          ))
        ) : (
          <p className="text-sm italic text-slate-400">
            {t("balance.noComments")}
          </p>
        )}
      </div>
    </div>
  );
}

export default BalanceSheetComments;
